import { Component } from '@theme/component';

const UK_TIME_ZONE = 'Europe/London';
const WORKING_DAYS = new Set(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
const MINUTES_PER_DAY = 24 * 60;
const REFRESH_MS = 30_000;
const DAY_MS = MINUTES_PER_DAY * 60_000;

/**
 * Live countdown to the next dispatch cutoff (Monday to Friday, UK time).
 * Counts down whenever that cutoff is less than 24 hours away; otherwise shows the closed message.
 * Dates listed in data-holidays (YYYY-MM-DD) are treated as non-working days.
 * When a delivery ref is present, it also shows estimated delivery dates counted in working days from dispatch.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [message] - The element whose text is updated.
 * @property {HTMLElement} [delivery] - Optional element for the estimated delivery dates.
 *
 * @extends {Component<Refs>}
 */
class DispatchCountdown extends Component {
  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    clearInterval(this.#intervalId);
    this.#intervalId = window.setInterval(() => this.#render(), REFRESH_MS);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#intervalId);
    this.#intervalId = undefined;
  }

  #render() {
    const { message } = this.refs;
    if (!message) return;

    const cutoffMinutes = (Number(this.dataset.cutoffHour) || 12) * 60;
    const holidays = new Set((this.dataset.holidays ?? '').split(/[\s,]+/).filter(Boolean));
    const nowMs = Date.now();
    const now = getUkTime(new Date(nowMs));
    const minutesNow = now.hour * 60 + now.minute;

    // Find the next working day whose cutoff has not passed yet (today counts if before the cutoff).
    // Bank holidays are skipped. Searching two weeks ahead covers the Christmas break.
    let daysAhead = 0;
    while (daysAhead < 14) {
      const day = daysAhead === 0 ? now : getUkTime(new Date(nowMs + daysAhead * MINUTES_PER_DAY * 60_000));
      const isWorkingDay = WORKING_DAYS.has(day.weekday) && !holidays.has(day.date);
      if (isWorkingDay && (daysAhead > 0 || minutesNow < cutoffMinutes)) break;
      daysAhead += 1;
    }

    const minutesLeft = daysAhead * MINUTES_PER_DAY + cutoffMinutes - minutesNow;
    const dispatchDate = daysAhead === 0 ? now.date : getUkTime(new Date(nowMs + daysAhead * DAY_MS)).date;

    this.#renderDelivery(dispatchDate, holidays);

    if (minutesLeft >= MINUTES_PER_DAY) {
      message.textContent = this.dataset.closedText ?? '';
      return;
    }

    const afterText = daysAhead === 0 ? this.dataset.afterText : this.dataset.afterTextNextDay;

    const hours = Math.floor(minutesLeft / 60);
    const minutes = minutesLeft % 60;
    const remaining = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

    const time = document.createElement('span');
    time.className = 'dispatch-countdown__time';
    time.textContent = remaining;

    message.replaceChildren(`${this.dataset.beforeText ?? ''} `, time, ` ${afterText ?? ''}`);
  }

  /**
   * Fills the delivery ref, e.g. "Standard: Wed 4 – Fri 6 Nov", from the dispatch date.
   * @param {string} dispatchDate - Dispatch day in UK time (YYYY-MM-DD).
   * @param {Set<string>} holidays
   */
  #renderDelivery(dispatchDate, holidays) {
    const { delivery } = this.refs;
    if (!delivery) return;

    const lines = [
      [this.dataset.standardLabel, this.dataset.standardDays],
      [this.dataset.expressLabel, this.dataset.expressDays],
    ];

    const parts = [];
    for (const [label, days] of lines) {
      const [min, max] = (days ?? '').split('-').map(Number);
      if (!label || !Number.isFinite(min)) continue;

      const from = addWorkingDays(dispatchDate, min, holidays);
      const to = addWorkingDays(dispatchDate, Number.isFinite(max) ? max : min, holidays);
      parts.push(`${label} ${formatRange(from, to)}`);
    }

    delivery.textContent = parts.join(' · ');
    delivery.hidden = parts.length === 0;
  }
}

/**
 * Adds working days (Monday to Friday, skipping holidays) to an ISO date.
 * @param {string} isoDate - YYYY-MM-DD
 * @param {number} count
 * @param {Set<string>} holidays
 * @returns {Date} The resulting day at 12:00 UTC.
 */
function addWorkingDays(isoDate, count, holidays) {
  const date = new Date(`${isoDate}T12:00:00Z`);
  let added = 0;
  while (added < count) {
    date.setUTCDate(date.getUTCDate() + 1);
    const weekday = date.getUTCDay();
    const iso = date.toISOString().slice(0, 10);
    if (weekday !== 0 && weekday !== 6 && !holidays.has(iso)) added += 1;
  }
  return date;
}

/**
 * Formats a date range such as "Wed 4 – Fri 6 Nov" or "Fri 30 Oct – Tue 3 Nov".
 * @param {Date} from
 * @param {Date} to
 * @returns {string}
 */
function formatRange(from, to) {
  /** @param {Date} date @param {boolean} withMonth */
  const format = (date, withMonth) =>
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      weekday: 'short',
      day: 'numeric',
      ...(withMonth ? { month: 'short' } : {}),
    }).format(date);

  if (from.getTime() === to.getTime()) return format(to, true);
  return `${format(from, from.getUTCMonth() !== to.getUTCMonth())} – ${format(to, true)}`;
}

/**
 * Returns the weekday, ISO date (YYYY-MM-DD), hour and minute for a date in UK time.
 * @param {Date} date
 * @returns {{ weekday: string, date: string, hour: number, minute: number }}
 */
function getUkTime(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIME_ZONE,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  /** @param {string} type */
  const part = (type) => parts.find((p) => p.type === type)?.value ?? '';

  return {
    weekday: part('weekday'),
    date: `${part('year')}-${part('month')}-${part('day')}`,
    hour: Number(part('hour')),
    minute: Number(part('minute')),
  };
}

if (!customElements.get('dispatch-countdown')) {
  customElements.define('dispatch-countdown', DispatchCountdown);
}
